import React, { useState, useEffect } from 'react';

const StripePaymentModal = ({ isOpen, onClose, invoice, vendor }) => {
  const amount = invoice?.depositAmount || invoice?.total || 0;
  const [loading, setLoading] = useState(false);
  const [stripe, setStripe] = useState(null);
  const [cardElement, setCardElement] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  useEffect(() => {
    if (isOpen && window.Stripe) {
      const stripeInstance = window.Stripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
      setStripe(stripeInstance);
      
      const elements = stripeInstance.elements();
      const card = elements.create('card', {
        hidePostalCode: true,
        style: {
          base: {
            fontSize: '16px',
            color: '#1F2937',
            '::placeholder': { color: '#9CA3AF' }
          }
        }
      });
      
      setTimeout(() => {
        const cardContainer = document.getElementById('card-element');
        if (cardContainer) {
          card.mount('#card-element');
          setCardElement(card);
        }
      }, 100);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handlePayment = async () => {
    if (!stripe || !cardElement) {
      alert('Payment form not ready');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: amount * 100,
          invoiceId: invoice.id,
          currency: 'usd'
        })
      });

      const data = await response.json();
      
      if (!data.clientSecret) {
        throw new Error('Failed to create payment intent');
      }
      
      const { error } = await stripe.confirmCardPayment(data.clientSecret, {
        payment_method: { card: cardElement }
      });

      if (error) {
        alert('Payment failed: ' + error.message);
      } else {
        // Update invoice status to paid
        console.log('Updating invoice:', invoice.id);
        const updateResponse = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/invoices/${invoice.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            status: 'paid',
            paidAt: new Date().toISOString(),
            paymentIntentId: data.clientSecret.split('_secret_')[0]
          })
        });
        
        if (!updateResponse.ok) {
          const errorText = await updateResponse.text();
          console.error('Failed to update invoice:', errorText);
          throw new Error('Payment succeeded but failed to update invoice status');
        }
        
        console.log('Invoice updated successfully');
        setPaymentSuccess(true);
        setTimeout(() => {
          onClose();
          setPaymentSuccess(false);
        }, 2000);
      }
    } catch (err) {
      alert('Payment error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1001
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '32px',
        width: '500px',
        maxHeight: '80vh',
        overflowY: 'auto'
      }}>
        {paymentSuccess ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{
              width: '60px',
              height: '60px',
              backgroundColor: '#6B9080',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <polyline points="20,6 9,17 4,12"></polyline>
              </svg>
            </div>
            <h3 style={{
              fontSize: '24px',
              fontWeight: '500',
              color: '#1F2937',
              marginBottom: '8px'
            }}>
              Payment Successful!
            </h3>
            <p style={{ fontSize: '16px', color: '#6B7280' }}>
              Your payment of ${amount} has been processed.
            </p>
          </div>
        ) : (
          <>
            <h3 style={{
              fontSize: '24px',
              fontWeight: '500',
              color: '#1F2937',
              marginBottom: '20px',
              fontFamily: 'Georgia, serif'
            }}>
              Pay with Credit Card
            </h3>
        
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Invoice</div>
          <div style={{ fontSize: '16px', color: '#1F2937' }}>{invoice?.name || 'Unnamed Invoice'}</div>
        </div>
        
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Vendor</div>
          <div style={{ fontSize: '16px', color: '#1F2937' }}>{vendor || 'Unknown'}</div>
        </div>
        
        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Amount Due</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1F2937' }}>${amount}</div>
        </div>
        
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '6px'
          }}>
            Card Details
          </label>
          <div id="card-element" style={{
            padding: '12px 16px',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            backgroundColor: '#F9FAFB'
          }}></div>
        </div>
        
        {!paymentSuccess && (
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              padding: '12px 24px',
              backgroundColor: 'white',
              color: '#6B7280',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handlePayment}
            disabled={loading}
            style={{
              padding: '12px 24px',
              backgroundColor: loading ? '#9CA3AF' : '#6B9080',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Processing...' : `Pay $${amount}`}
          </button>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default StripePaymentModal;
